import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import useSignalement from "../../hooks/useSignalement";
import useArticles from "../../hooks/useArticle";
import useUtilisateur from "../../hooks/useUtilisateur";
import Breadcrumb from "../../components/breadcrumb/Breadcrumb";
import "../../assets/css/signalement/AdminSignalementRecapPage.css";
import { imageArticleUrl } from "../../utils/modules";
import noImage from "../../assets/images/articles/no-photo.png";

export default function AdminSignalementRecapPage({ setAlert }) {
  const {
    getOneSignalement,
    signalementRecap,
    updateSignalement,
    validateSignalement,
    rejectSignalement,
  } = useSignalement();
  const { updateArticleStatus } = useArticles();
  const { updateOneUser } = useUtilisateur();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const id = searchParams.get("id");
  const from = location.state?.from;

  const [signalement, setSignalement] = useState(null);
  const [loading, setLoading] = useState(false);

  // recap totals
  const [recap, setRecap] = useState(null);
  const [loadingRecap, setLoadingRecap] = useState(false);

  // overlay preuve
  const [preuvePreview, setPreuvePreview] = useState(null);

  // slider images article + overlay zoom article
  const [activeImg, setActiveImg] = useState(0);
  const [articlePreview, setArticlePreview] = useState(null);

  // actions admin
  const [deactivatingArticle, setDeactivatingArticle] = useState(false);
  const [deactivatingUser, setDeactivatingUser] = useState(false);
  const [proprietaire, setProprietaire] = useState();

  const goBack = () => {
    if (from) return navigate(from, { replace: true });
    return navigate(`/admin/signalements`);
  };

  useEffect(() => {
    if (!id) return;

    // 1) détail signalement
    setLoading(true);
    getOneSignalement(id)
      .then((res) => setSignalement(res?.data?.data || null))
      .catch(() => {
        setAlert?.({
          type: "danger",
          message: "Impossible de charger le signalement",
        });
      })
      .finally(() => setLoading(false));

    // 2) recap (totaux)
    setLoadingRecap(true);
    signalementRecap(id)
      .then((res) => {
        const payload = res?.data?.data ?? res?.data ?? null;
        setRecap(payload);
        setProprietaire(payload?.utilisateur?.id || null);
      })
      .catch(() => setRecap(null))
      .finally(() => setLoadingRecap(false));
  }, [id]);

  // reset slider quand article change
  useEffect(() => {
    setActiveImg(0);
  }, [signalement?.idArticle?._id]);

  const badge = useMemo(() => {
    const t = Number(signalement?.typeSignalement);
    if (t === 1) return { label: "Fraude", cls: "recapBadge recapBadgeDanger" };
    if (t === 2)
      return {
        label: "Contenu inapproprié",
        cls: "recapBadge recapBadgeWarning",
      };
    return { label: "Autre", cls: "recapBadge recapBadgeNeutral" };
  }, [signalement?.typeSignalement]);

  const formatDate = (d) => {
    if (!d) return "—";
    try {
      return new Date(d).toLocaleString("fr-FR");
    } catch {
      return "—";
    }
  };

  if (loading) {
    return (
      <div className="container mt-5 mb-5 adminRecapWrap">
        <div className="recapLoading">
          <div className="spinner-border" role="status" />
          <span>Chargement du signalement…</span>
        </div>
      </div>
    );
  }

  if (!signalement) return null;

  const signaleur = signalement?.idSignaleur;
  const article = signalement?.idArticle;

  const msg = signalement?.message || signalement?.description || "—";

  // preuve
  const preuveFile =
    signalement?.preuve ||
    signalement?.preuveImage ||
    signalement?.justification ||
    signalement?.file ||
    signalement?.filename ||
    signalement?.photo ||
    signalement?.image ||
    null;

  const preuveUrl = preuveFile ? `${imageArticleUrl}/${preuveFile}` : null;

  // images article
  const articleImages = Array.isArray(article?.photos) ? article.photos : [];
  const totalImages = articleImages.length;
  const safeIndex = totalImages ? Math.min(activeImg, totalImages - 1) : 0;
  const currentImgUrl = totalImages
    ? `${imageArticleUrl}/${articleImages[safeIndex]}`
    : noImage;

  const nextImg = () => {
    if (!totalImages) return;
    setActiveImg((p) => (p + 1) % totalImages);
  };

  const prevImg = () => {
    if (!totalImages) return;
    setActiveImg((p) => (p - 1 + totalImages) % totalImages);
  };

  // totaux recap
  const totalArticleSignalements = recap?.article?.totalSignalements;

  // ✅ IMPORTANT : c’est le total "reçu par l’utilisateur cible" (propriétaire de l’annonce)
  const totalUtilisateurSignalements = recap?.utilisateur?.totalSignalements;

  const displayCount = (v) => {
    if (loadingRecap) return "…";
    if (v == null) return "—";
    return String(v);
  };

  // ✅ utilisateur ciblé (celui qui reçoit les signalements) :
  // priorité: recap.utilisateur.id -> article.idUtilisateur -> signalement.idUtilisateur
  const utilisateurCible =
    recap?.utilisateur?.id ||
    article?.idUtilisateur ||
    signalement?.idUtilisateur ||
    null;

  const utilisateurCibleNom =
    `${utilisateurCible?.nom || ""} ${utilisateurCible?.prenom || ""}`.trim() ||
    "—";

  // ✅ IDs utiles
  const articleId = article?._id || recap?.article?.id?._id;
  const userId =
    recap?.utilisateur?.id?._id ||
    article?.idUtilisateur?._id ||
    signalement?.idUtilisateur?._id ||
    null;

  // ✅ flags (si tes objets contiennent déjà ces champs)
  const isArticleAlreadyDisabled =
    article?.archive === 1 && article?.status === 2;
  const isUserAlreadyDisabled =
    recap?.utilisateur?.id?.archive === 1 &&
    recap?.utilisateur?.id?.active === 2;

  const handleToggleArticle = async (mode) => {
    if (!articleId) return;

    const isDisable = mode === "disable";

    setDeactivatingArticle(true);

    try {
      await Promise.all([
        updateArticleStatus(articleId, "archive", isDisable ? 1 : 2),
        updateArticleStatus(articleId, "status", isDisable ? 2 : 1),
      ]);

      setAlert?.({
        type: "success",
        message: isDisable
          ? "Annonce désactivée avec succès."
          : "Annonce activée avec succès.",
      });

      // Mise à jour locale
      setSignalement((prev) =>
        prev
          ? {
              ...prev,
              idArticle: {
                ...(prev.idArticle || {}),
                archive: isDisable ? 1 : 2,
                status: isDisable ? 2 : 1,
              },
            }
          : prev,
      );
    } catch (e) {
      setAlert?.({
        type: "danger",
        message: isDisable
          ? "Échec de désactivation de l’annonce."
          : "Échec d’activation de l’annonce.",
      });
    } finally {
      setDeactivatingArticle(false);
    }
  };

  const handleToggleUser = async (mode) => {
    if (!userId) return;

    const isDisable = mode === "disable";
    const USER_SOURCE = "?source=utilisateur";

    setDeactivatingUser(true);

    try {
      await updateOneUser(USER_SOURCE, userId, {
        archive: isDisable ? 1 : 2,
        active: isDisable ? 2 : 1,
      });

      setAlert?.({
        type: "success",
        message: isDisable
          ? "Utilisateur désactivé avec succès."
          : "Utilisateur activé avec succès.",
      });

      // ✅ Mise à jour temps réel
      setRecap((prev) =>
        prev
          ? {
              ...prev,
              utilisateur: {
                ...prev.utilisateur,
                id: {
                  ...(prev.utilisateur?.id || {}),
                  archive: isDisable ? 1 : 2,
                  active: isDisable ? 2 : 1,
                },
              },
            }
          : prev,
      );
    } catch (e) {
      setAlert?.({
        type: "danger",
        message: isDisable
          ? "Échec de désactivation de l’utilisateur."
          : "Échec d’activation de l’utilisateur.",
      });
    } finally {
      setDeactivatingUser(false);
    }
  };

  const handleSignalementAction = async (mode, s) => {
    if (!s?._id) return;

    const isValidate = mode === "validate";
    const newStatus = isValidate ? 2 : 3;

    try {
      let res;

      if (isValidate) {
        res = await validateSignalement(s._id);
      } else {
        res = await rejectSignalement(s._id);
      }

      const userDisabled = res?.data?.data?.stats?.userDisabled;

      setAlert?.({
        type: "success",
        message: isValidate
          ? userDisabled
            ? "Signalement validé. L'utilisateur a été désactivé."
            : "Signalement validé."
          : "Signalement rejeté.",
      });
      setSignalement((prev) =>
        prev
          ? {
              ...prev,
              status: newStatus,
            }
          : prev,
      );
    } catch (e) {
      setAlert?.({
        type: "danger",
        message: isValidate
          ? "Impossible de valider."
          : "Impossible de rejeter.",
      });
    }
  };

  return (
    <div className="container mt-5 mb-5 adminRecapWrap">
      <i
        className="bi bi-x-circle login_icon_x"
        role="button"
        onClick={goBack}
        title="Fermer"
      />

      <Breadcrumb />

      <div className="article_create_header recapHeader">
        <div className="recapHeaderTop">
          <h1 className="article_create_title">Récapitulatif du signalement</h1>
          <div className="recapMeta">
            <span className={badge.cls}>
              <i className="bi bi-flag-fill" /> {badge.label}
            </span>
            <span className="recapMetaItem">
              <i className="bi bi-clock-history" />{" "}
              {formatDate(signalement?.createdAt)}
            </span>
          </div>
        </div>

        <p className="recapSubtitle">
          Consulte les informations du signaleur, de l’annonce signalée et le
          détail du message.
        </p>
      </div>

      <div className="row g-4 mt-1">
        {/* COL GAUCHE */}
        <div className="col-12 col-lg-6">
          {/* PROPRIETAIRE DE L'ANNONCE */}
          <div className="recapCard">
            <div className="recapCardTitleRow">
              <div className="recapCardTitle">
                <i className="bi bi-person" /> Propriétaire de l’annonce
              </div>
              {/* ✅ FIX: afficher clairement que le total est "reçu pour l’utilisateur ciblé" */}
              <span className="recapCountPill recapCountPillUser">
                <i className="bi bi-flag" />{" "}
                {displayCount(totalUtilisateurSignalements)} signalement(s)
                reçu(s)
              </span>
              {isUserAlreadyDisabled && (
                <span className="recapBadge recapBadgeDanger">
                  <i className="bi bi-person-x" /> Désactivé
                </span>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">Nom & Prénom</label>
              <input
                className="form-control custom_css_field_update"
                value={
                  `${proprietaire?.nom || ""} ${proprietaire?.prenom || ""}`.trim() ||
                  "—"
                }
                readOnly
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">Email</label>
              <input
                className="form-control custom_css_field_update"
                value={proprietaire?.email || "—"}
                readOnly
              />
            </div>

            {/* <div>
              <label className="form-label fw-bold">ID utilisateur</label>
              <input
                className="form-control custom_css_field_update"
                value={proprietaire?._id || "—"}
                readOnly
              />
            </div> */}
          </div>

          {/* ANNONCE */}
          <div className="recapCard mt-4">
            <div className="recapCardTitleRow">
              <div className="recapCardTitle">
                <i className="bi bi-box-seam" /> Annonce signalée
              </div>

              <span className="recapCountPill recapCountPillArticle">
                <i className="bi bi-flag" />{" "}
                {displayCount(totalArticleSignalements)} signalement(s)
              </span>
            </div>

            <div className="recapArticlePreview">
              <div className="recapArticleThumb recapArticleThumbSlider">
                <button
                  type="button"
                  className="recapNavBtn recapNavLeft"
                  onClick={prevImg}
                  disabled={!totalImages || totalImages === 1}
                  title="Précédent"
                >
                  <i className="bi bi-chevron-left" />
                </button>

                <img
                  src={currentImgUrl}
                  alt={article?.titre || "Annonce"}
                  onClick={() => setArticlePreview(currentImgUrl)}
                  role="button"
                  title="Cliquer pour agrandir"
                  onError={(e) => {
                    e.currentTarget.src = noImage;
                  }}
                />

                <button
                  type="button"
                  className="recapNavBtn recapNavRight"
                  onClick={nextImg}
                  disabled={!totalImages || totalImages === 1}
                  title="Suivant"
                >
                  <i className="bi bi-chevron-right" />
                </button>

                {totalImages > 1 && (
                  <div className="recapImgCounter">
                    {safeIndex + 1}/{totalImages}
                  </div>
                )}
              </div>

              <div className="recapArticleInfo">
                <div className="recapArticleTitleRow">
                  <div className="recapArticleTitle">
                    {article?.titre || "—"}
                  </div>
                </div>

                <div className="recapArticleDesc">
                  {article?.description || "—"}
                </div>

                <div className="recapArticleMeta">
                  {article?.prix != null && (
                    <span className="recapMetaChip">
                      <i className="bi bi-cash-coin" /> {article?.prix}
                    </span>
                  )}
                  {article?.ville && (
                    <span className="recapMetaChip">
                      <i className="bi bi-geo-alt" /> {article?.ville}
                    </span>
                  )}
                </div>

                {totalImages > 1 && (
                  <div className="recapMiniThumbs">
                    {articleImages.slice(0, 6).map((p, idx) => {
                      const url = `${imageArticleUrl}/${p}`;
                      const active = idx === safeIndex;
                      return (
                        <img
                          key={p + idx}
                          className={`recapMiniThumb ${active ? "active" : ""}`}
                          src={url}
                          alt={`thumb ${idx + 1}`}
                          onClick={() => setActiveImg(idx)}
                          onError={(e) => {
                            e.currentTarget.src = noImage;
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* LISTE SIGNALEUR */}
          <div className="recapCard mt-4 recapListSignaleur">
            <div className="recapCardTitle">
              <i className="bi bi-person-badge" /> Signaleurs
            </div>
            <div className="recapSignaleurList">
              {recap?.article?.signaleurs?.map((res) => (
                <div
                  key={res.signalementId}
                  className="btn recapSignaleurItem"
                  onClick={async () => {
                    getOneSignalement(res?.signalementId)
                      .then((res) => {
                        const signalementData = res?.data?.data || null;
                        if (signalementData) {
                          setSignalement(signalementData);
                          setRecap(null);
                          setLoadingRecap(true);
                          signalementRecap(signalementData._id)
                            .then((res) => {
                              const payload =
                                res?.data?.data ?? res?.data ?? null;
                              setRecap(payload);
                            })
                            .catch(() => setRecap(null))
                            .finally(() => setLoadingRecap(false));
                        } else {
                          setAlert?.({
                            type: "warning",
                            message: "Détails du signalement indisponibles.",
                          });
                        }
                      })
                      .catch(() => setRecap(null))
                      .finally(() => setLoadingRecap(false));
                  }}
                >
                  <span className="recapBadge recapBadgeWarning">
                    {res.signaleur.nom} {res.signaleur.prenom}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* COL DROITE */}
        <div className="col-12 col-lg-6">
          {/* SIGNALEUR */}
          <div className="recapCard">
            <div className="recapCardTitleRow">
              <div className="recapCardTitle">
                <i className="bi bi-person-badge" /> Signaleur
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">Nom & Prénom</label>
              <input
                className="form-control custom_css_field_update"
                value={
                  `${signaleur?.nom || ""} ${signaleur?.prenom || ""}`.trim() ||
                  "—"
                }
                readOnly
              />
            </div>

            <div className="mb-0">
              <label className="form-label fw-bold">Email</label>
              <input
                className="form-control custom_css_field_update"
                value={signaleur?.email || "—"}
                readOnly
              />
            </div>
          </div>

          <div className="recapCard mt-4">
            <div className="recapCardTitle">
              <i className="bi bi-exclamation-triangle" /> Détails
            </div>

            <div className="row g-3">
              <div className="col-12 col-md-6">
                <label className="form-label fw-bold">Type</label>
                <input
                  className="form-control custom_css_field_update"
                  value={badge.label}
                  readOnly
                />
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label fw-bold">Date</label>
                <input
                  className="form-control custom_css_field_update"
                  value={formatDate(signalement?.createdAt)}
                  readOnly
                />
              </div>
            </div>

            <div className="mt-3">
              <label className="form-label fw-bold">Message</label>
              <textarea
                className="form-control custom_css_field_update recapTextarea"
                rows={6}
                value={msg}
                readOnly
              />
            </div>

            {/* PREUVE */}
            <div className="mt-3">
              <label className="form-label fw-bold d-flex align-items-center gap-2">
                <i className="bi bi-camera" /> Preuve (facultatif)
              </label>

              {!preuveUrl ? (
                <div className="recapEmptyProof">Aucune preuve jointe.</div>
              ) : (
                <div className="recapProofWrap">
                  <div
                    className="recapProofThumb"
                    role="button"
                    title="Cliquer pour agrandir"
                    onClick={() => setPreuvePreview(preuveUrl)}
                  >
                    <img
                      src={preuveUrl}
                      alt="Preuve"
                      onError={(e) => {
                        e.currentTarget.src = noImage;
                      }}
                    />
                    <div className="recapProofHover">
                      <i className="bi bi-zoom-in" /> Agrandir
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ✅ Actions admin */}
          <div className="recapCard mt-4">
            <div className="recapCardTitle">
              <i className="bi bi-shield-lock" /> Actions administrateur
            </div>

            <div className="recapAdminActions">
              <button
                className={
                  isArticleAlreadyDisabled
                    ? "recapSuccessBtn"
                    : "recapDangerBtn"
                }
                onClick={() =>
                  handleToggleArticle(
                    isArticleAlreadyDisabled ? "activate" : "disable",
                  )
                }
                disabled={!articleId || deactivatingArticle}
              >
                {deactivatingArticle ? (
                  <>
                    <span className="spinner-border spinner-border-sm" />
                    <span>Traitement…</span>
                  </>
                ) : isArticleAlreadyDisabled ? (
                  <>
                    <i className="bi bi-check-circle" />
                    <span>Activer l’annonce</span>
                  </>
                ) : (
                  <>
                    <i className="bi bi-ban" />
                    <span>Désactiver l’annonce</span>
                  </>
                )}
              </button>

              <button
                className={
                  isUserAlreadyDisabled
                    ? "recapSuccessBtn"
                    : "recapDangerOutlineBtn"
                }
                onClick={() =>
                  handleToggleUser(
                    isUserAlreadyDisabled ? "activate" : "disable",
                  )
                }
                disabled={!userId || deactivatingUser}
              >
                {deactivatingUser ? (
                  <>
                    <span className="spinner-border spinner-border-sm" />
                    <span>Traitement…</span>
                  </>
                ) : isUserAlreadyDisabled ? (
                  <>
                    <i className="bi bi-person-check" />
                    <span>Activer l’utilisateur</span>
                  </>
                ) : (
                  <>
                    <i className="bi bi-person-x" />
                    <span>Désactiver l’utilisateur</span>
                  </>
                )}
              </button>

              <button
                type="button"
                className={
                  Number(signalement?.status) === 2
                    ? "recapSuccessBtn"
                    : "recapOutlineSuccessBtn"
                }
                onClick={() => handleSignalementAction("validate", signalement)}
              >
                <i className="bi bi-check-lg" />
                <span>
                  {Number(signalement?.status) === 2
                    ? "✔ Validé (cliquer pour confirmer)"
                    : "Valider le signalement"}
                </span>
              </button>

              <button
                type="button"
                className={
                  Number(signalement?.status) === 3
                    ? "recapDangerBtn"
                    : "recapOutlineSuccessBtn"
                }
                onClick={() => handleSignalementAction("reject", signalement)}
              >
                <i className="bi bi-x-lg" />
                <span>
                  {Number(signalement?.status) === 3
                    ? "✖ Rejeté (cliquer pour modifier)"
                    : "Rejeter le signalement"}
                </span>
              </button>
            </div>
          </div>

          <div className="recapActions mt-4">
            <button className="btn recapBtnLight" onClick={goBack}>
              <i className="bi bi-arrow-left" /> Retour à la liste
            </button>

            <button
              className="article_create_btn_continuer"
              onClick={() => navigate(`/article/details/${articleId}`)}
              disabled={!articleId}
              style={{ width: 240 }}
            >
              Voir l’annonce
            </button>
          </div>
        </div>
      </div>

      {/* Overlay preuve */}
      {preuvePreview && (
        <div
          className="image-preview-overlay"
          onClick={() => setPreuvePreview(null)}
        >
          <img src={preuvePreview} alt="preuve" />
        </div>
      )}

      {/* Overlay article */}
      {articlePreview && (
        <div
          className="image-preview-overlay"
          onClick={() => setArticlePreview(null)}
        >
          <img src={articlePreview} alt="article" />
        </div>
      )}
    </div>
  );
}
